import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { Button, Input } from 'reactstrap';
import Modal from '../../../common/modal/Modal';
import Table from '../../../common/Table';
import FormFooter from '../../../common/forms/FormFooter';
import BusMaintenanceStayForm from './BusMaintenanceStayForm';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import {
  BUS_MAINTENANCE_STATUS_TYPE,
  COLOR,
  DEFAULT_PAGE_SIZE,
} from '../../../../config/constants';
import { tzNormalizeDate } from '../../../../utils/date';
import {
  DATE_TIME_FORMAT,
  INT_DATE_TIME_FORMAT,
} from '../../../../config/locale';

const getBusId = (busGroup) => busGroup.registeredBusId || busGroup.busId;

const getBusNumber = (busGroup) =>
  busGroup.companyBusId || busGroup.busNumber || getBusId(busGroup) || '-';

const getBusKey = (busGroup) =>
  String(getBusId(busGroup) || busGroup.companyBusId || busGroup.busNumber);

const getSelectValue = (value) =>
  value && typeof value === 'object' ? value.value : value;

const formatDate = (date) =>
  date
    ? tzNormalizeDate({
        date,
        format: DATE_TIME_FORMAT,
      })
    : '-';

const normalizeStay = (busGroup, stay) => {
  const registeredBusId = getBusId(busGroup);
  const companyBusId = getBusNumber(busGroup);

  return {
    id: stay.id,

    registeredBusId,

    registeredBus: registeredBusId
      ? {
          id: registeredBusId,
          companyBusId,
        }
      : null,

    originCityId: stay.originId,

    originCity: stay.originId
      ? {
          id: stay.originId,
          name: stay.originName,
        }
      : null,

    destinationCityId: stay.destinationId,

    destinationCity: stay.destinationId
      ? {
          id: stay.destinationId,
          name: stay.destinationName,
        }
      : null,

    maintenanceStayStatus: stay.status,

    estimatedArrivalDate: stay.arrivalDate,

    estimatedCompletionDate: stay.completionDate,

    actualDeliveryTime: stay.deliveryDate,

    noArrivalTime: stay.noArrivalTime || false,

    observation: stay.observation,

    modified: false,
    markedForDelete: false,
  };
};

const normalizeGroups = (groups) =>
  groups.map((busGroup) => ({
    ...busGroup,

    busKey: getBusKey(busGroup),

    busId: getBusId(busGroup),

    busNumber: getBusNumber(busGroup),

    stays: (busGroup.stays || []).map((stay) => normalizeStay(busGroup, stay)),
  }));

const generateStyle = (row) => {
  if (!row || !row.original) {
    return {};
  }

  if (row.original.modified) {
    return {
      background: COLOR.success,
    };
  }

  if (row.original.noArrivalTime) {
    return {
      background: COLOR.warning,
    };
  }

  return {};
};

const generateInitialValues = (row) => ({
  id: row.id,

  registeredBusId:
    row.registeredBusIdSelect ||
    (row.registeredBusId
      ? {
          value: row.registeredBusId,
          label:
            (row.registeredBus && row.registeredBus.companyBusId) ||
            row.registeredBusId,
        }
      : null),

  originCityId:
    row.originCityIdSelect ||
    (row.originCityId
      ? {
          value: row.originCityId,
          label: (row.originCity && row.originCity.name) || row.originCityId,
        }
      : null),

  maintenanceStayStatus:
    row.maintenanceStayStatusSelect ||
    (row.maintenanceStayStatus
      ? BUS_MAINTENANCE_STATUS_TYPE[row.maintenanceStayStatus]
      : null),

  observation: row.observation,

  estimatedArrivalDate:
    row.estimatedArrivalDate != null
      ? tzNormalizeDate({
          date: row.estimatedArrivalDate,
          format: INT_DATE_TIME_FORMAT,
        })
      : null,

  estimatedCompletionDate:
    row.estimatedCompletionDate != null
      ? tzNormalizeDate({
          date: row.estimatedCompletionDate,
          format: INT_DATE_TIME_FORMAT,
        })
      : null,

  noArrivalTime: row.noArrivalTime || false,
});

const generateExtendedMaintenanceColumns = ({
  busKey,
  handleEdit,
  handleClear,
}) => [
  {
    Header: 'ID',
    accessor: 'id',
    className: 'text-center',
    width: 90,
  },
  {
    Header: 'Origen',
    accessor: 'originCity.name',
    Cell: (row) => row.value || '-',
    className: 'text-center',
    width: 180,
  },
  {
    Header: 'Destino',
    accessor: 'destinationCity.name',
    Cell: (row) => row.value || '-',
    className: 'text-center',
    width: 180,
  },
  {
    Header: 'Fecha de Llegada',
    accessor: 'estimatedArrivalDate',
    Cell: (row) => formatDate(row.value),
    className: 'text-center',
    filterable: false,
    width: 200,
  },
  {
    Header: 'Fecha de Entrega',
    accessor: 'estimatedCompletionDate',
    Cell: (row) => formatDate(row.value),
    className: 'text-center',
    filterable: false,
    width: 200,
  },
  {
    Header: 'Estado',
    accessor: 'maintenanceStayStatus',
    Cell: (row) =>
      row.value
        ? (BUS_MAINTENANCE_STATUS_TYPE[row.value] &&
            BUS_MAINTENANCE_STATUS_TYPE[row.value].label) ||
          row.value
        : '-',
    className: 'text-center',
    width: 220,
  },
  {
    Header: 'Observación',
    accessor: 'observation',
    Cell: (row) => row.value || '-',
    className: 'text-center',
    minWidth: 250,
  },
  {
    Header: 'Acción',
    accessor: 'id',
    Cell: (cell) => (
      <div className="d-flex justify-content-center align-items-center">
        <Button
          color="info"
          outline
          className="mr-3"
          title="Editar"
          onClick={() => handleEdit(busKey, cell.original)}
        >
          <i className="fa fa-pencil" />
        </Button>

        {cell.original.modified && (
          <Button
            color="warning"
            outline
            title="Deshacer cambios"
            onClick={() => handleClear(busKey, cell.original)}
          >
            <i className="fa fa-times" />
          </Button>
        )}
      </div>
    ),
    className: 'text-center',
    filterable: false,
    width: 180,
  },
];

const ExtendedBusMaintenanceModal = ({
  show,
  onClose,
  extendedMaintenance,
  onSaveBus,
  loading,
}) => {
  const [groups, setGroups] = useState([]);
  const [originalGroups, setOriginalGroups] = useState([]);

  const [modifiedByBus, setModifiedByBus] = useState({});

  const [selectedBusKey, setSelectedBusKey] = useState(null);

  const [busSearch, setBusSearch] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedEditBusKey, setSelectedEditBusKey] = useState(null);

  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    const source = Immutable.List.isList(extendedMaintenance)
      ? extendedMaintenance.toJS()
      : extendedMaintenance || [];

    const normalizedGroups = normalizeGroups(source);

    setGroups(normalizedGroups);
    setOriginalGroups(normalizedGroups);
    setModifiedByBus({});

    setSelectedBusKey((currentBusKey) => {
      const currentBusExists = normalizedGroups.some(
        (group) => group.busKey === currentBusKey,
      );

      if (currentBusExists) {
        return currentBusKey;
      }

      return normalizedGroups.length > 0 ? normalizedGroups[0].busKey : null;
    });
  }, [extendedMaintenance]);

  useEffect(() => {
    if (!show) {
      setShowEditModal(false);
      setSelectedEditBusKey(null);
      setSelectedRow(null);
      setBusSearch('');
    }
  }, [show]);

  const selectedBus = groups.find((group) => group.busKey === selectedBusKey);

  const filteredGroups = groups.filter((group) => {
    const searchValue = busSearch.trim().toLowerCase();

    if (!searchValue) {
      return true;
    }

    return String(group.busNumber).toLowerCase().includes(searchValue);
  });

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedEditBusKey(null);
    setSelectedRow(null);
  };

  const handleEdit = (busKey, row) => {
    setSelectedEditBusKey(busKey);
    setSelectedRow(row);
    setShowEditModal(true);
  };

  const handleSubmitEdit = (formValues) => {
    if (!selectedEditBusKey || !selectedRow) {
      return;
    }

    const originCityId = getSelectValue(formValues.originCityId);

    const registeredBusId = getSelectValue(formValues.registeredBusId);

    const maintenanceStayStatus = getSelectValue(
      formValues.maintenanceStayStatus,
    );

    const updatedRow = {
      ...selectedRow,

      registeredBusId,

      registeredBusIdSelect: formValues.registeredBusId,

      registeredBus:
        formValues.registeredBusId &&
        typeof formValues.registeredBusId === 'object'
          ? {
              id: registeredBusId,
              companyBusId: formValues.registeredBusId.label,
            }
          : selectedRow.registeredBus,

      originCityId,

      originCityIdSelect: formValues.originCityId,

      originCity:
        formValues.originCityId && typeof formValues.originCityId === 'object'
          ? {
              id: originCityId,
              name: formValues.originCityId.label,
            }
          : selectedRow.originCity,

      maintenanceStayStatus,

      maintenanceStayStatusSelect: formValues.maintenanceStayStatus,

      estimatedArrivalDate: formValues.estimatedArrivalDate,

      estimatedCompletionDate: formValues.estimatedCompletionDate,

      observation: formValues.observation,

      noArrivalTime: formValues.noArrivalTime || false,

      modified: true,
      markedForDelete: false,
    };

    setGroups((currentGroups) =>
      currentGroups.map((group) => {
        if (group.busKey !== selectedEditBusKey) {
          return group;
        }

        return {
          ...group,
          stays: group.stays.map((stay) =>
            stay.id === updatedRow.id ? updatedRow : stay,
          ),
        };
      }),
    );

    setModifiedByBus((current) => {
      const currentBusChanges = current[selectedEditBusKey] || [];

      const existingIndex = currentBusChanges.findIndex(
        (stay) => stay.id === updatedRow.id,
      );

      const newBusChanges = [...currentBusChanges];

      if (existingIndex >= 0) {
        newBusChanges[existingIndex] = updatedRow;
      } else {
        newBusChanges.push(updatedRow);
      }

      return {
        ...current,
        [selectedEditBusKey]: newBusChanges,
      };
    });

    closeEditModal();
  };

  const handleClear = (busKey, row) => {
    const originalGroup = originalGroups.find(
      (group) => group.busKey === busKey,
    );

    if (!originalGroup) {
      return;
    }

    const originalRow = originalGroup.stays.find((stay) => stay.id === row.id);

    if (!originalRow) {
      return;
    }

    setGroups((currentGroups) =>
      currentGroups.map((group) => {
        if (group.busKey !== busKey) {
          return group;
        }

        return {
          ...group,
          stays: group.stays.map((stay) =>
            stay.id === row.id ? originalRow : stay,
          ),
        };
      }),
    );

    setModifiedByBus((current) => {
      const newBusChanges = (current[busKey] || []).filter(
        (stay) => stay.id !== row.id,
      );

      return {
        ...current,
        [busKey]: newBusChanges,
      };
    });
  };

  const handleSaveBus = async (busKey) => {
    const modifiedRows = modifiedByBus[busKey] || [];

    if (modifiedRows.length === 0) {
      return;
    }

    await onSaveBus(modifiedRows);

    setModifiedByBus((current) => ({
      ...current,
      [busKey]: [],
    }));
  };

  const handleCloseMainModal = () => {
    closeEditModal();
    onClose();
  };

  const selectedModifiedRows = selectedBus
    ? modifiedByBus[selectedBus.busKey] || []
    : [];

  const body = (
    <div
      className="d-flex"
      style={{
        height: '80vh',
        overflow: 'hidden',
      }}
    >
      <div
        className="pr-3"
        style={{
          width: '270px',
          minWidth: '270px',
          overflowY: 'auto',
          borderRight: '1px solid #dee2e6',
        }}
      >
        <div className="mb-2 font-weight-bold">Buses ({groups.length})</div>

        <Input
          type="text"
          value={busSearch}
          className="mb-3"
          placeholder="Buscar número de bus"
          onChange={(event) => setBusSearch(event.target.value)}
        />

        {filteredGroups.length === 0 && (
          <div className="text-muted text-center py-3">
            No se encontraron buses.
          </div>
        )}

        {filteredGroups.map((busGroup) => {
          const modifiedRows = modifiedByBus[busGroup.busKey] || [];

          const isSelected = busGroup.busKey === selectedBusKey;

          return (
            <Button
              key={busGroup.busKey}
              type="button"
              color={isSelected ? 'primary' : 'light'}
              className="d-flex justify-content-between align-items-center w-100 mb-1 text-left"
              onClick={() => setSelectedBusKey(busGroup.busKey)}
            >
              <span>Bus {busGroup.busNumber}</span>

              <span className="d-flex align-items-center">
                <span
                  className={
                    isSelected ? 'badge badge-light' : 'badge badge-warning'
                  }
                >
                  {busGroup.stays.length}
                </span>

                {modifiedRows.length > 0 && (
                  <span className="badge badge-success ml-1">
                    {modifiedRows.length}
                  </span>
                )}
              </span>
            </Button>
          );
        })}
      </div>

      <div
        className="pl-3 flex-grow-1"
        style={{
          overflowY: 'auto',
          minWidth: 0,
        }}
      >
        {!selectedBus && (
          <div className="text-center py-4">Seleccione un bus.</div>
        )}

        {selectedBus && (
          <>
            <div className="d-flex align-items-center flex-wrap mb-3">
              <h5 className="mb-0">Bus: {selectedBus.busNumber}</h5>

              <span className="badge badge-warning ml-2">
                {selectedBus.stays.length}{' '}
                {selectedBus.stays.length === 1
                  ? 'mantenimiento'
                  : 'mantenimientos'}
              </span>

              {selectedModifiedRows.length > 0 && (
                <span className="badge badge-success ml-2">
                  {selectedModifiedRows.length}{' '}
                  {selectedModifiedRows.length === 1
                    ? 'cambio pendiente'
                    : 'cambios pendientes'}
                </span>
              )}
            </div>

            <Table
              manual={false}
              columns={generateExtendedMaintenanceColumns({
                busKey: selectedBus.busKey,
                handleEdit,
                handleClear,
              })}
              data={selectedBus.stays}
              filterable
              defaultFilterMethod={filterMethodCaseInsensitive}
              loading={loading}
              generateStyle={generateStyle}
              defaultPageSize={Math.min(
                Math.max(selectedBus.stays.length, 5),
                DEFAULT_PAGE_SIZE,
              )}
            />

            <div className="mt-3">
              <FormFooter
                saveButtonAction={() => handleSaveBus(selectedBus.busKey)}
                saveButtonDisabled={
                  loading || selectedModifiedRows.length === 0
                }
                saveButtonText={
                  selectedModifiedRows.length > 0
                    ? `Guardar Cambios del Bus (${selectedModifiedRows.length})`
                    : 'Guardar Cambios del Bus'
                }
                saveButtonType="button"
              />
            </div>
          </>
        )}

        <div className="mt-3 text-right">
          <Button
            type="button"
            color="secondary"
            onClick={handleCloseMainModal}
          >
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );

  const editBody = selectedRow ? (
    <BusMaintenanceStayForm
      edit
      initialValues={generateInitialValues(selectedRow)}
      onCancel={closeEditModal}
      onSubmit={handleSubmitEdit}
    />
  ) : null;

  const selectedEditBus = groups.find(
    (group) => group.busKey === selectedEditBusKey,
  );

  return (
    <>
      <Modal
        show={show}
        title="Mantenimientos Extendidos por Bus"
        onClickClose={handleCloseMainModal}
        body={body}
        size="xl"
        fullSize
      />

      <Modal
        show={showEditModal}
        title={
          selectedRow
            ? `Editar Mantenimiento Extendido - Bus ${
                selectedEditBus ? selectedEditBus.busNumber : '-'
              } - ID ${selectedRow.id}`
            : 'Editar Mantenimiento Extendido'
        }
        onClickClose={closeEditModal}
        body={editBody}
        size="lg"
      />
    </>
  );
};

ExtendedBusMaintenanceModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaveBus: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  extendedMaintenance: PropTypes.oneOfType([
    PropTypes.instanceOf(Immutable.List),
    PropTypes.arrayOf(PropTypes.object),
  ]),
};

ExtendedBusMaintenanceModal.defaultProps = {
  show: false,
  loading: false,
  extendedMaintenance: Immutable.List(),
};

export default ExtendedBusMaintenanceModal;
